const MAPLE_BASE_Y = 0.5;

export type LeafKind = "maple" | "ovate";

const MAPLE_PROFILE: readonly (readonly [number, number])[] = [
  [-100, 0.16],
  [-80, 0.7],
  [-60, 0.34],
  [-40, 0.92],
  [-20, 0.42],
  [-10, 0.74],
  [0, 1.06],
  [10, 0.74],
  [20, 0.42],
  [40, 0.92],
  [60, 0.34],
  [80, 0.7],
  [100, 0.16],
];

const MAPLE_POINTS: readonly (readonly [number, number])[] = MAPLE_PROFILE.map(([degrees, distance]) => {
  const radians = (degrees * Math.PI) / 180;
  return [Math.sin(radians) * distance, MAPLE_BASE_Y - Math.cos(radians) * distance] as const;
});

const MAPLE_TIPS: readonly (readonly [number, number])[] = [
  [0, 1.06],
  [-40, 0.92],
  [40, 0.92],
  [-80, 0.7],
  [80, 0.7],
];

function traceLeafPath(context: CanvasRenderingContext2D, kind: LeafKind, radius: number): void {
  if (kind === "ovate") {
    context.beginPath();
    context.moveTo(0, -radius);
    context.bezierCurveTo(radius * 0.62, -radius * 0.55, radius * 0.6, radius * 0.45, 0, radius * 0.9);
    context.bezierCurveTo(-radius * 0.6, radius * 0.45, -radius * 0.62, -radius * 0.55, 0, -radius);
    context.closePath();
    return;
  }
  context.beginPath();
  for (let index = 0; index < MAPLE_POINTS.length; index++) {
    const [x, y] = MAPLE_POINTS[index];
    if (index === 0) {
      context.moveTo(x * radius, y * radius);
    } else {
      context.lineTo(x * radius, y * radius);
    }
  }
  context.closePath();
}

function traceLeafVeins(context: CanvasRenderingContext2D, kind: LeafKind, radius: number): void {
  context.beginPath();
  if (kind === "ovate") {
    context.moveTo(0, radius * 0.9);
    context.lineTo(0, radius * 1.3);
    context.moveTo(0, -radius);
    context.lineTo(0, radius * 0.9);
    context.moveTo(0, -radius * 0.35);
    context.lineTo(radius * 0.42, -radius * 0.05);
    context.moveTo(0, -radius * 0.35);
    context.lineTo(-radius * 0.42, -radius * 0.05);
    context.moveTo(0, radius * 0.1);
    context.lineTo(radius * 0.4, radius * 0.4);
    context.moveTo(0, radius * 0.1);
    context.lineTo(-radius * 0.4, radius * 0.4);
    return;
  }
  const baseY = MAPLE_BASE_Y * radius;
  context.moveTo(0, baseY);
  context.lineTo(0, baseY + radius * 0.42);
  for (const [degrees, distance] of MAPLE_TIPS) {
    const radians = (degrees * Math.PI) / 180;
    const directionX = Math.sin(radians);
    const directionY = -Math.cos(radians);
    context.moveTo(directionX * radius * 0.1, baseY + directionY * radius * 0.1);
    context.lineTo(directionX * distance * radius * 0.8, baseY + directionY * distance * radius * 0.8);
  }
}

export function drawLeafShape(
  context: CanvasRenderingContext2D,
  color: (alpha: number) => string,
  kind: LeafKind,
  radius: number,
  alpha: number
): void {
  const isMaple = kind === "maple";
  const strokeAlpha = alpha * (isMaple ? 0.55 : 1);

  traceLeafPath(context, kind, radius);
  context.fillStyle = color(alpha * 0.72);
  context.fill();
  context.strokeStyle = color(strokeAlpha);
  context.lineJoin = "round";
  context.lineCap = "round";
  context.lineWidth = Math.max(0.8, radius * (isMaple ? 0.062 : 0.09));
  context.stroke();

  context.strokeStyle = color(strokeAlpha * 0.6);
  traceLeafVeins(context, kind, radius);
  context.stroke();
}
